//@flow
import React, {useEffect, useState} from 'react';

import {makeStyles, ButtonBase} from '@material-ui/core';

import type {ThemeColor} from '../../../../../../../../entities';
import {colors} from '../../../../../../../../assets/styles/colors';
import {Close} from '@material-ui/icons';
import {useCurrentConversation} from '../../../../../utils/conversation';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    position: 'absolute',
    top: '-3.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  label: {
    paddingInline: '.5rem',
    fontWeight: 600,
  },

  notificationButton: {
    padding: '.5rem',
    background: 'white',
    boxShadow: 'rgba(17, 12, 46, 0.1) 0px 12px 32px 0px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '5rem',
    transition: 'transform 0.2s ease-in-out, boxShadow 0.2s ease-in-out',
    backdropFilter: 'blur(2rem)',

    '&:active': {
      transform: 'scale(0.98)',
      boxShadow: 'rgba(17, 12, 46, 0) 0px 0 0 0px',
    },

    '&[data-theme="primary"]': {
      background: `${colors.primary}20`,
      color: colors.primary,
    },
    '&[data-theme="secondary"]': {
      background: `${colors.secondary}20`,
      color: colors.secondary,
    },
    '&[data-theme="success"]': {
      background: `${colors.success}20`,
      color: colors.success,
    },
    '&[data-theme="warning"]': {
      background: `${colors.warning}20`,
      color: colors.warning,
    },
    '&[data-theme="info"]': {
      background: `${colors.info}20`,
      color: colors.info,
    },
    '&[data-theme="danger"]': {
      background: `${colors.danger}20`,
      color: colors.danger,
    },
  },
  count: {
    width: '24px',
    height: '24px',
    fontWeight: 600,
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&[data-theme="primary"]': {
      background: colors.primary,
      color: 'white',
    },
    '&[data-theme="secondary"]': {
      background: colors.secondary,
      color: 'white',
    },
    '&[data-theme="success"]': {
      background: colors.success,
      color: 'white',
    },
    '&[data-theme="warning"]': {
      background: colors.warning,
      color: 'white',
    },
    '&[data-theme="info"]': {
      background: colors.info,
      color: 'white',
    },
    '&[data-theme="danger"]': {
      background: colors.danger,
      color: 'white',
    },
  },
  icon: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.35rem',
  },
  closeButton: {
    width: '24px',
    height: '24px',
    color: 'white',
    background: colors.danger,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export type Props = {
  label: string,
  color: ThemeColor,
  icon?: React$Node,
  onClick?: () => void,
  count?: number | string,
  onClose?: () => void,
};

export default function BottomNotification(props: Props) {
  const {label, color = 'primary', icon, onClick, count, onClose} = props;
  const classes = useStyles();

  const [showCloseButton, setShowCloseButton] = useState(false);
  const [show, setShow] = useState(true);

  const {currentConversation} = useCurrentConversation();

  useEffect(() => {
    setShow(true);
    setShowCloseButton(false);
  }, [currentConversation]);

  if (!show) return null;

  const handleCloseClick = () => {
    setShow(false);
    onClose && onClose();
  };

  return (
    <aside className={classes.root}>
      <ButtonBase
        onClick={onClick}
        className={classes.notificationButton}
        data-theme={color}
      >
        {icon && (
          <span className={classes.icon} data-theme={color}>
            {icon}
          </span>
        )}
        <span className={classes.label} data-theme={color}>
          {label}
        </span>

        {count && !showCloseButton && (
          <span
            className={classes.count}
            data-theme={color}
            onMouseEnter={() => setShowCloseButton(true)}
          >
            {count}
          </span>
        )}
        {showCloseButton && (
          <ButtonBase
            className={classes.closeButton}
            onClick={e => {
              e.stopPropagation();
              handleCloseClick();
            }}
            onMouseLeave={() => setShowCloseButton(false)}
          >
            <Close fontSize="small" />
          </ButtonBase>
        )}
      </ButtonBase>
    </aside>
  );
}
